/**
 * EloMigrationPanel
 * Admin-only panel that runs the 2026_ELO_FIX migration on demand.
 * Shows a side-by-side before/after report and offers a one-click rollback.
 *
 * Backend endpoints used:
 *   POST /api/admin/run-elo-migration         (admin + super password)
 *   GET  /api/admin/elo-migration/status      (admin)
 *   POST /api/admin/elo-migration/rollback    (admin + super password)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, Database, ChartLineUp, ChartLineDown, Warning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { callWithSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function DeltaBadge({ value, suffix = "", invert = false }) {
  if (value == null || isNaN(value)) return null;
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  const color = positive ? "text-chart-3" : negative ? "text-destructive" : "text-muted-foreground";
  const sign = value > 0 ? "+" : "";
  return <span className={`text-xs font-mono ${color}`}>{sign}{value}{suffix}</span>;
}

export default function EloMigrationPanel() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/elo-migration/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to load migration status", err);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runMigration = async () => {
    if (!window.confirm(
      "Run the 2026 ELO + Ranking migration?\n\n" +
      "This will:\n" +
      "• Back up every player's current ELO/ranking\n" +
      "• Reset ELO to 1500\n" +
      "• Replay every completed match chronologically\n" +
      "• Rebuild placement points (with time decay)\n" +
      "• Recompute ranks and tiers\n\n" +
      "No matches or tournaments will be modified.\n" +
      "This is safe to re-run."
    )) return;

    setRunning(true);
    setReport(null);
    try {
      const result = await callWithSuperPassword(
        ({ headers }) => axios.post(
          `${API}/admin/run-elo-migration`,
          {},
          { headers, withCredentials: true, timeout: 120_000 },
        ),
        {
          title: "Confirm ELO migration",
          description: "Enter Super Password to run the migration. Idempotent — a backup row is added per run.",
          actionLabel: "Run migration",
        },
      );
      if (!result) return; // Admin cancelled password prompt
      setReport(result.data);
      toast.success("Migration complete");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Migration failed");
    } finally {
      setRunning(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm(
      "Restore the most recent backup?\n\n" +
      "This will revert every player's ELO/ranking/tier to the snapshot taken before the last migration run. " +
      "Matches and tournaments will NOT be touched."
    )) return;
    setRollingBack(true);
    try {
      const result = await callWithSuperPassword(
        ({ headers }) => axios.post(
          `${API}/admin/elo-migration/rollback`,
          {},
          { headers, withCredentials: true, timeout: 60_000 },
        ),
        {
          title: "Confirm rollback",
          description: "Enter Super Password to restore from backup.",
          actionLabel: "Rollback",
        },
      );
      if (!result) return;
      toast.success(result.data?.message || "Restored");
      setReport(null);
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="elo-migration-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-secondary" />
          ELO + Ranking Migration
          <Badge variant="outline" className="text-[10px] font-mono ml-2">2026_ELO_FIX</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground leading-relaxed">
          Backs up every player's current ELO, ranking, and tier into <code className="text-[10px] bg-muted px-1 rounded">players_backup_migration_2026</code> with a unique <code className="text-[10px] bg-muted px-1 rounded">run_id</code>,
          then resets ELO to <strong>1500</strong> and replays every completed match in chronological
          order using the canonical formula (K-factor by experience, 0.05–0.95 clamped expected, frame-diff margin bonus).
          Placement points are rebuilt with tier × time decay, ranks and tiers are reassigned. <strong>Idempotent</strong>: re-running the migration is always safe.
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Last run:</span>
          <span data-testid="elo-migration-last-run">
            {status?.last_run ? fmtDate(status.last_run.ran_at) : "Never"}
          </span>
          {status?.last_run?.run_id && (
            <span className="text-[10px] font-mono text-muted-foreground">
              ({status.last_run.run_id.slice(0, 8)})
            </span>
          )}
          {status?.last_run?.counts && (
            <span className="text-muted-foreground ml-2">
              · {status.last_run.counts.players_updated} players · {status.last_run.counts.matches_replayed} matches replayed
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={runMigration}
            disabled={running || rollingBack}
            data-testid="run-elo-migration-btn"
          >
            <ArrowClockwise className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} weight="bold" />
            {running ? "Running…" : status?.last_run ? "Re-run Migration" : "Run Migration"}
          </Button>
          {status?.last_run && (
            <Button
              variant="outline"
              onClick={rollback}
              disabled={running || rollingBack}
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid="rollback-elo-migration-btn"
            >
              {rollingBack ? "Restoring…" : "Rollback Last Run"}
            </Button>
          )}
        </div>

        {report && <ReportDisplay report={report} />}
      </CardContent>
    </Card>
  );
}

function ReportDisplay({ report }) {
  const anomalyCount = (report.anomalies || []).length;
  return (
    <div className="space-y-4 border-t border-border pt-4" data-testid="elo-migration-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
        <StatBox label="Players updated" value={report.players_updated} />
        <StatBox label="Matches replayed" value={report.matches_replayed} />
        <StatBox label="Matches skipped" value={report.matches_skipped} />
        <StatBox label="Placements applied" value={report.placements_applied} />
      </div>

      {anomalyCount > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs">
          <div className="flex items-center gap-2 text-destructive font-medium mb-1">
            <Warning className="w-4 h-4" /> {anomalyCount} anomaly{anomalyCount > 1 ? "ies" : ""} detected
          </div>
          <ul className="space-y-0.5 text-destructive/80 ml-6 list-disc">
            {report.anomalies.slice(0, 10).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <BeforeAfterTopTen
        before={report.before_top_10 || []}
        after={report.after_top_10 || []}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MoversList
          title="Biggest ELO Gainers"
          icon={<ChartLineUp className="w-4 h-4 text-chart-3" />}
          rows={report.biggest_elo_gainers}
          deltaKey="delta"
          deltaSuffix=" ELO"
        />
        <MoversList
          title="Biggest ELO Losers"
          icon={<ChartLineDown className="w-4 h-4 text-destructive" />}
          rows={report.biggest_elo_losers}
          deltaKey="delta"
          deltaSuffix=" ELO"
        />
      </div>

      <RankMovers rows={report.biggest_rank_movers || []} />
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/30 p-2.5">
      <div className="text-lg font-heading font-bold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function BeforeAfterTopTen({ before, after }) {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-2">Top 10 — Before vs After</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Before</TableHead>
            <TableHead>After</TableHead>
            <TableHead className="text-right">ΔELO</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: Math.max(before.length, after.length, 10) }).map((_, i) => {
            const b = before[i];
            const a = after[i];
            const eloDelta = a && b && a.id === b.id ? Math.round((a.elo_after - b.elo) * 10) / 10 : null;
            return (
              <TableRow key={i} data-testid={`top10-row-${i}`}>
                <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                <TableCell className="text-xs">
                  {b ? (
                    <span>
                      {b.name} <span className="text-muted-foreground">({Math.round(b.elo)})</span>
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs">
                  {a ? (
                    <span>
                      {a.name} <span className="text-muted-foreground">({Math.round(a.elo_after)})</span>
                      {a.tier_after !== a.tier_before && (
                        <Badge variant="outline" className="ml-1 text-[9px] py-0">
                          {a.tier_before} → {a.tier_after}
                        </Badge>
                      )}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {eloDelta != null ? <DeltaBadge value={eloDelta} /> : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function MoversList({ title, icon, rows, deltaKey, deltaSuffix }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">{icon} {title}</h4>
      <ul className="space-y-1 text-xs">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex items-center justify-between gap-2">
            <span className="truncate">{r.name || r.id}</span>
            <DeltaBadge value={r[deltaKey]} suffix={deltaSuffix} />
          </li>
        ))}
        {(!rows || rows.length === 0) && (
          <li className="text-muted-foreground italic">No data</li>
        )}
      </ul>
    </div>
  );
}

function RankMovers({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-border p-3">
      <h4 className="text-xs font-semibold mb-2">Biggest Rank Movers</h4>
      <ul className="space-y-1 text-xs">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center justify-between gap-2">
            <span className="truncate">{r.name || r.id}</span>
            <span className="font-mono text-xs">
              #{r.old_rank ?? "?"} → #{r.new_rank}
              <DeltaBadge value={r.delta} invert />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
